// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
  type QueryReturnValue,
} from '@reduxjs/toolkit/query';
import { brokerageApi } from '@shared/data-access/api-base/citec-brokerage-api';
import { isUndefined } from '@shared/utils';
import type {
  AggregatorProposalParams,
  InternalCreateTargetPortfolioRequestBody,
  TargetPortfolioResponse,
  CreateAggregatorProposalRequestBody,
} from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import {
  selectClientInstitutions,
  selectClientNuvaraInversisInstitution,
} from '@shared/data-access/slices/clients';
import {
  buildTraderTargetPortfolio,
  type CreateTargetPortfolioResult,
} from '@shared/data-access/transformers/proposals/build-trader-target-portfolio';
import { mapTargetPortfolioRequest } from '@shared/data-access/transformers/proposals/map-target-portfolio-request';
import { mapInvestmentProposalRequest } from '@shared/data-access/transformers/proposals/map-investment-proposal-request';
import type { ProposalScopedInstitutionAccounts } from '@shared/data-access/transformers/proposals/build-proposal-payload';

export type CreateProposalMutationArgs = AggregatorProposalParams &
  CreateAggregatorProposalRequestBody & {
    /**
     * Proposal-scoped institutions/accounts (IBANs + client cash). Used only to
     * build the aggregator payload; not sent in the POST body.
     */
    advisory_proposal_accounts: readonly ProposalScopedInstitutionAccounts[];
  };

/**
 * Aggregator Proposals API - Citec Brokerage Integration.
 *
 * This API layer provides access to the **Citec Brokerage REST API** for creating
 * client trading proposals via the aggregator module. The aggregator allows for the
 * consolidation of trading positions across multiple client accounts and
 * facilitates integration with external brokers, banks, or custodians.
 *
 * @remarks
 * Use this API to:
 *
 * - Submit new trading proposals containing one or more operations.
 * - Create target portfolios from current portfolio positions and operations.
 *
 * Backed by RTK Query, this module abstracts RESTful operations into React hooks
 * for seamless integration with front-end applications.
 * @see {@link https://citecsolutions.com/apis} for full API reference.
 */
export const proposalsApi = brokerageApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Create a new trading proposal for a client.
     *
     * @remarks
     * This endpoint allows the creation of a new aggregator proposal, including one
     * or more trading operations. The client context is passed via query
     * parameters.
     * @example
     *
     * ```tsx
     * const [createProposal] = proposalsApi.useCreateProposalMutation();
     * createProposal({
     *   client_id: 123,
     *   name: 'Growth Strategy Q3',
     *   description: 'Focus on tech sector',
     *   currency: 'USD',
     *   operations: [ ... ],
     *   current_portfolio: { ... },
     *   proposal: { ... },
     *   returns: { ... },
     *   risk_returns: [ ... ]
     * });
     * ```
     *
     * @param body - An object containing the `client_id` and the request body,
     *   which includes the name, description (optional), currency, operations,
     *   current_portfolio, proposal, returns, and risk_returns. Mapped to
     *   {@link CreateInvestmentProposalRequestBody} before POST.
     * @returns Void response (201) upon successful creation.
     */
    createProposal: build.mutation<void, CreateProposalMutationArgs>({
      queryFn: async (params, queryApi, _extraOptions, fetchWithBQ) => {
        const { buildProposalPayload } = await import(
          '@shared/data-access/transformers/proposals/build-proposal-payload'
        );

        const nuvaraInversisInstitution = selectClientNuvaraInversisInstitution(
          queryApi.getState() as any,
        );

        if (isUndefined(nuvaraInversisInstitution)) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Nuvara Inversis account required to send proposals',
            } as FetchBaseQueryError,
          };
        }

        const result = await buildProposalPayload(
          {
            clientId: params.client_id,
            name: params.name,
            description: params.description ?? '',
            operations: params.operations,
            currency: params.currency,
            riskProfile: params.risk_profile,
            advisoryProposalAccounts: params.advisory_proposal_accounts,
            nuvara_inversis: nuvaraInversisInstitution.products.accounts,
          },
          queryApi.dispatch,
        );

        if (result.error) {
          return {
            error: result.error as FetchBaseQueryError,
          };
        }

        const payload = mapInvestmentProposalRequest(result.data);

        return fetchWithBQ({
          url: '/v2/advisory/investment-proposals/',
          method: 'POST',
          body: payload,
          params: { client_id: params.client_id },
        }) as QueryReturnValue<void, FetchBaseQueryError, FetchBaseQueryMeta>;
      },
      invalidatesTags: (_result, _error, { client_id }) => [
        { type: 'Proposals', id: client_id ?? 'List' },
      ],
    }),

    /**
     * Simulate a target portfolio from current positions and operations.
     *
     * @remarks
     * Posts to `POST /v2/advisory/proposals/target-portfolio/`. Returns enriched
     * `positions` plus simulation `status` and `operation_results`. HTTP 200 is not
     * success: callers must require `status === 'success'` and every
     * `operation_results[].status === 'applied'`.
     * @example
     *
     * ```tsx
     * const { data } = proposalsApi.useCreateTargetPortfolioQuery({
     *   current_portfolio: [ ... ],
     *   operations: [ ... ],
     * });
     * ```
     *
     * @param body - An object containing the current portfolio positions and
     *   operations to be executed. Must conform to
     *   {@link InternalCreateTargetPortfolioRequestBody} (positions and operations
     *   must include institution_id for internal use).
     * @returns Enriched positions together with simulation status and per-operation
     *   results.
     */
    createTargetPortfolio: build.query<
      CreateTargetPortfolioResult,
      InternalCreateTargetPortfolioRequestBody
    >({
      queryFn: async (params, queryApi, _extraOptions, fetchWithBQ) => {
        const response = (await fetchWithBQ({
          url: '/v2/advisory/proposals/target-portfolio/',
          method: 'POST',
          body: mapTargetPortfolioRequest(params),
        })) as QueryReturnValue<
          TargetPortfolioResponse,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        >;

        if (response.error) {
          return {
            error: response.error as FetchBaseQueryError,
          };
        }

        const institutions = selectClientInstitutions(queryApi.getState() as any);

        const positions = await buildTraderTargetPortfolio(
          response.data?.positions ?? [],
          params.current_portfolio,
          institutions,
          queryApi.dispatch,
        );

        return {
          data: {
            positions,
            status: response.data?.status ?? 'failed',
            operation_results: response.data?.operation_results ?? [],
          },
        };
      },
      extraOptions: {
        useTrackPromise: true,
      },
      providesTags: [{ type: 'Proposals', id: 'TargetPortfolio' }],
    }),
  }),
});
